/**
 * The legal group carried by every surface's action bar.
 *
 * These are an OBLIGATION, not content, which is why they live in one place
 * rather than being passed in per surface. A publisher configuring a hub does
 * not get to choose whether their page carries a privacy policy, and a surface
 * that forgot to pass them would be a compliance gap created by an omission —
 * exactly the kind of failure that is invisible until it matters.
 *
 * Cookie preferences is the one CONTROL among them: it reopens the consent
 * manager rather than navigating, so it is last (everything before it is a
 * thing to read) and it never drops at a breakpoint, because a preferences
 * link that is only reachable on a wide screen is not a preferences link.
 *
 * OPEN, and deliberately not decided here: on a CUSTOM_DOMAIN, do these point
 * at DevourPlay's documents or the operator's? Today they are ours, because
 * ours is what governs the platform the surface runs on — but a white-label
 * customer may have their own, and that is a contract question rather than a
 * component one. See the embedded-consent strategy work.
 */

import type {SurfaceActionBarLegalLink} from "./SurfaceActionBar";

export const SURFACE_LEGAL_LINKS: readonly SurfaceActionBarLegalLink[] = [
    {id: "terms", label: "Terms", href: "/terms-of-service"},
    {id: "privacy", label: "Privacy", href: "/privacy-policy"},
    {
        id: "cookie-prefs",
        /*
         * "Cookies", not "Cookie preferences". Three words in a 10px footnote
         * overflowed the menu panel and clipped, and the cookie POLICY is
         * linked from Privacy — so the one thing that needs its own place here
         * is the control, and this is it.
         */
        label: "Cookies",
        isControl: true,
        /*
         * Termly's consent manager exposes a global to reopen its banner. Guarded
         * because Termly is deliberately suppressed on embedded and partner
         * surfaces — where it is absent the control must do nothing rather than
         * throw, and the absence is the correct behaviour, not a bug to code
         * around.
         */
        onClick: () => {
            const termly = (window as unknown as { displayPreferenceModal?: () => void }).displayPreferenceModal;
            termly?.();
        },
    },
];

/**
 * The account menu's own items, above the legal group.
 *
 * Deliberately the platform's account actions and nothing surface-specific: a
 * hub operator does not get to put their own entries in a viewer's account
 * menu, because it is the viewer's relationship with DevourPlay and not with
 * this Drop. Anything about the surface belongs in the surface — the rail, the
 * stage, the sticky bar.
 *
 * OPEN: sign-out needs the auth action wired, and "Profile" should point at
 * the viewer's own Drop once that route is settled. Left as hrefs rather than
 * guessed handlers.
 */
/*
 * The account menu, matched to the brand-challenge menus rather than invented.
 *
 * RTTR is nickname → My Profile → Help Chat → Sign Out; Full Throttle is
 * nickname → Invite a Friend / Edit Profile / Help Chat → Log Out. The shape
 * both share: the member's name first, then things that happen IN CONTEXT,
 * then leaving, set apart.
 *
 * The first version of this was "Profile" and "Settings" pointing at /account —
 * which is exactly what RTTR's own comment records them removing, because the
 * links navigated people out of the experience and into the platform app. A
 * hub is somebody else's surface; sending their visitor to our account pages
 * from their menu is the same mistake.
 *
 * Handlers are supplied by the surface, not here: this file knows WHAT the
 * menu offers, and only the page knows how to open a modal or dispatch a chat.
 */
export interface SurfaceAccountAction {
    id: string;
    label: string;
    emphasis?: string;
}

export const SURFACE_ACCOUNT_ACTIONS: readonly SurfaceAccountAction[] = [
    {id: "edit-profile", label: "Edit profile"},
    {id: "invite", label: "Invite a friend"},
    {id: "help", label: "Help chat"},
    {id: "signout", label: "Sign out", emphasis: "sh-avm__out"},
];

/*
 * The brand-challenge menu set, exactly — name row, My profile, Help chat,
 * Sign out. No invite: the brand campaigns never carried one, and matching
 * their menu COMPLETELY is the ruling (Shelly, 2026-09-01).
 */
export const BRAND_ACCOUNT_ACTIONS: readonly SurfaceAccountAction[] = [
    {id: "edit-profile", label: "My profile"},
    {id: "help", label: "Help chat"},
    {id: "signout", label: "Sign out", emphasis: "sh-avm__out"},
];
